import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_API_URL } from '../../constants';

/**
 * 이메일 인증 관련 로직을 담당하는 커스텀 훅
 * 커스텀 이메일 시스템만 사용
 * @returns {object} - 이메일 인증 관련 상태와 함수들
 */
export const useEmailVerification = () => {
  const { i18n } = useTranslation();
  const [isResending, setIsResending] = useState(false);

  /**
   * 커스텀 이메일 재발송
   */
  const sendCustomVerificationEmail = async (userEmail, displayName) => {
    try {
      const currentLanguage = i18n.language;
      
      const requestBody = {
        email: userEmail,
        displayName: displayName || '사용자',
        language: currentLanguage || 'ko'
      };
      
      const response = await fetch(`${AI_API_URL}/api/email-auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ 이메일 인증 재발송 성공');
        return true;
      } else {
        console.error('❌ 이메일 인증 재발송 실패:', data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ 이메일 인증 재발송 API 호출 실패:', error);
      return false;
    }
  };

  /**
   * 이메일 인증 재발송 함수
   * @param {Object} user - Firebase 사용자 객체
   * @returns {Promise<boolean>} - 성공 여부
   */
  const resendEmailVerification = async (user) => {
    if (!user) {
      console.warn('⚠️ 사용자 정보를 찾을 수 없습니다.');
      return false;
    }

    if (user.emailVerified) {
      console.log('ℹ️ 이미 이메일 인증이 완료되었습니다.');
      return true;
    }

    try {
      setIsResending(true);
      
      const success = await sendCustomVerificationEmail(user.email, user.displayName || '사용자');
      if (success) {
        return true;
      } else {
        console.error('❌ 이메일 재발송에 실패했습니다.');
        return false;
      }
    } catch (error) {
      console.error('❌ 이메일 인증 재발송 실패:', error);
      
      // Firebase 에러 코드에 따른 로깅
      let errorMessage = '인증 이메일 발송에 실패했습니다.';
      
      switch (error.code) {
        case 'auth/too-many-requests':
          errorMessage = '너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.';
          break;
        case 'auth/user-disabled':
          errorMessage = '계정이 비활성화되었습니다.';
          break;
        case 'auth/user-not-found':
          errorMessage = '사용자를 찾을 수 없습니다.';
          break;
        default:
          errorMessage = `인증 이메일 발송 실패: ${error.message}`;
      }
      
      console.error('❌ 재발송 에러:', errorMessage);
      return false;
    } finally {
      setIsResending(false);
    }
  };

  return {
    isResending,
    resendEmailVerification
  };
}; 